import {
  getSPDataWithParams,
  postSPDataWithoutParams,
  postSPFormDataWithoutParams,
} from './services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const BASE_PATH = '/api/secure/cc/support/contact-actions'

function getResponseMessage(response, fallbackMessage) {
  return (
    response?.data?.message ||
    response?.response?.data?.message ||
    response?.message ||
    fallbackMessage
  )
}

function buildPayload(context = {}) {
  return {
    patientId: context?.patientId || undefined,
    practitionerId: context?.practitionerId || undefined,
    bookedEventId: context?.bookedEventId || undefined,
    clinicBrandId: context?.clinicBrandId || undefined,
    clinicBrandName: context?.clinicBrandName || undefined,
    clinicId: context?.clinicId || undefined,
    targetRole: context?.targetRole || undefined,
    eventType: context?.eventType || undefined,
    eventTypesCsv: context?.eventTypesCsv || undefined,
  }
}

export async function getSupportAgentContactCapabilities(context = {}) {
  const response = await getSPDataWithParams(`${BASE_PATH}/capabilities`, buildPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response)
    return {
      ok: true,
      data,
      channels: data?.channels || {},
      targetRole: data?.targetRole,
      targetDisplayName: data?.targetDisplayName,
      resolvedEventTypes: data?.resolvedEventTypes || [],
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load support contact capabilities.'),
    rawResponse: response,
  }
}

export async function sendSupportAgentWhatsappIntro(context = {}) {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/whatsapp/intro`, buildPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the WhatsApp intro.'),
    rawResponse: response,
  }
}

export async function getSupportAgentWhatsappThread(context = {}) {
  const response = await getSPDataWithParams(`${BASE_PATH}/whatsapp/thread`, buildPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response)
    return {
      ok: true,
      data,
      messages: data?.messages || [],
      templateActions: data?.templateActions || [],
      channels: data?.channels || {},
      freeFormAllowed: !!data?.freeFormAllowed,
      targetDisplayName: data?.targetDisplayName,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load the WhatsApp conversation.'),
    rawResponse: response,
  }
}

export async function getSupportAgentWhatsappThreads() {
  const response = await getSPDataWithParams(`${BASE_PATH}/whatsapp/threads`, {})

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response)
    return {
      ok: true,
      data,
      threads: data?.threads || [],
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load support WhatsApp threads.'),
    rawResponse: response,
  }
}

export async function sendSupportAgentWhatsappTemplate(context = {}, templateAction = '') {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/whatsapp/template`, {
    ...buildPayload(context),
    templateAction,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the WhatsApp template.'),
    rawResponse: response,
  }
}

export async function sendSupportAgentWhatsappMessage(context = {}, message = '') {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/whatsapp/message`, {
    ...buildPayload(context),
    message,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the WhatsApp message.'),
    rawResponse: response,
  }
}

export async function prepareSupportAgentEmail(context = {}) {
  const response = await postSPDataWithoutParams(`${BASE_PATH}/email/prepare`, buildPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to prepare the email draft.'),
    rawResponse: response,
  }
}

export async function sendSupportAgentWhatsappAttachment(context = {}, message = "", files = []) {
  const formData = new FormData()
  const payload = buildPayload(context)

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}` !== "") {
      formData.append(key, value)
    }
  })

  if (message) {
    formData.append("caption", message)
  }

  ;(files || []).forEach((file) => {
    if (file) {
      formData.append("files", file)
    }
  })

  const response = await postSPFormDataWithoutParams(`${BASE_PATH}/whatsapp/attachment`, formData)

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, "Unable to send the WhatsApp attachment."),
    rawResponse: response,
  }
}
